import { STORY_SOURCE, story } from '@/content/storyBuilder'

// The fourth written page, and the first whose header carries a photograph
// rather than the mark. Everything below comes from the four-part full-page
// capture of digitopia.co/customers/intel-ai/ sent on 2026-08-28 — "birebir
// tıpa tıp aynısını tasarla" — and the four photographs on it came with it.
// The closing quote is not a `section`: Victoria Kouyoumjian's words went into
// `content/testimonials.ts` keyed to this slug, which is where the other three
// written pages keep theirs, and `StoryBody` draws that card.
export const added = story('intel-ai', 'Intel AI', {
  doc: '424d9551-631e-44c4-ac81-6a923aed6a6d-Intel AI and Digitopia - Fueling Scalable AI '
    + 'Adoption with Data-Led Strategy.pdf',
  lede: [
    'How Intel AI uses Digitopia’s self-service platform to empower customers, drive '
    + 'engagement, and build trust through actionable insights.',
  ],
  heroPhoto: {
    src: '/photo/intel-ai-assessment-on-desk.webp',
    alt: 'A man at a desk reading his AI Readiness Assessment results, a list of '
      + 'recommendations beside a priorities chart, on an iMac, in a daylit open office.',
  },
  intro: [
    'To help organizations accelerate their AI journey, Intel AI partnered with Digitopia to '
    + 'launch a co-branded AI Readiness Assessment, hosted on Digitopia’s platform. The tool '
    + 'enables users to assess their AI maturity independently and receive tailored guidance, '
    + 'surfacing practical opportunities for growth.',
    'Built as a fully digital, self-service experience, the assessment not only delivers '
    + 'actionable insights but also positions Intel as a trusted AI advisor, opening the door '
    + 'to long-term engagement and collaboration.',
  ],
  sections: [
    {
      heading: 'The Challenge',
      centred: true,
      body: [
        'AI is a top priority for many organizations, but turning ambition into action is '
        + 'rarely straightforward. Most teams lack a clear understanding of where they stand '
        + 'or how to move forward confidently.',
        'Intel AI set out to solve this by:',
      ],
      items: [
        {
          text: 'Empowering customers and prospects with a self-guided tool to evaluate AI '
            + 'readiness.',
        },
        { text: 'Delivering early value with practical, actionable insights.' },
        { text: 'Building trust and credibility in a rapidly evolving AI ecosystem.' },
        { text: 'Enabling ongoing conversations around AI transformation.' },
      ],
      photo: {
        src: '/photo/intel-ai-night-city-edge-cloud.webp',
        alt: 'A city skyline at night, lit windows to the horizon, with white outlined '
          + 'squares drawn over two of the towers.',
      },
    },
    {
      heading: 'The Solution',
      band: true,
      photo: {
        src: '/photo/intel-ai-assessment-in-open-office.webp',
        alt: 'A man reading the assessment’s recommendations and priorities chart on an iMac '
          + 'in an open-plan office.',
      },
      photoSide: 'right',
      photoInPanel: true,
      body: [
        'Intel AI launched the AI Readiness Assessment as a key component of its '
        + 'customer-facing strategy. Integrated into campaigns, email nurtures, and field '
        + 'activities, the tool allowed users to:',
      ],
      items: [
        {
          text: 'Evaluate their AI maturity across six core dimensions: Strategy, Value, '
            + 'Data, Technology, Capabilities, and Governance.',
        },
        {
          text: 'Access dimension-specific guidance and curated content tailored to their '
            + 'results.',
        },
        {
          text: 'Gain insights through real-time dashboards and a personalized downloadable '
            + 'report.',
        },
      ],
      after: [
        'The co-branded experience is designed to be scalable across marketing, sales, and '
        + 'customer engagement functions worldwide.',
      ],
    },
    {
      heading: 'The Impact',
      band: true,
      photo: {
        src: '/photo/intel-ai-woman-working-on-laptop.webp',
        alt: 'A woman working on a laptop at a café window counter, a grid of pale blue '
          + 'squares drawn over the scene.',
      },
      photoSide: 'left',
      // One list of seven rather than two lists of four and three: the
      // reference sets "For Intel AI:" and "For Clients:" as bold labels, and
      // `item.lead` is bold already, so the grouping survives without a
      // second `items` array on `StorySection` that no other story wants.
      items: [
        {
          lead: 'For Intel AI',
          text: 'Strengthened its role as a trusted strategic advisor in AI transformation.',
        },
        { text: 'Generated qualified leads through value-led engagement.' },
        { text: 'Fostered deeper conversations with clients using data-backed insights.' },
        {
          text: 'Built a repeatable, scalable activation model for AI ecosystem enablement.',
        },
        {
          lead: 'For Clients',
          text: 'Achieved clarity on their AI maturity and key development areas.',
        },
        { text: 'Accelerated alignment on strategy and organizational priorities.' },
        {
          text: 'Gained the tools and structure to begin or expand their AI transformation '
            + 'journey with confidence.',
        },
      ],
    },
  ],
  check:
    'This page’s words were transcribed from a four-part full-page capture of '
    + `${STORY_SOURCE}/intel-ai/ supplied on 2026-08-28, not from a file we hold, so please `
    + 'proofread it against the source before production — particularly the six dimensions '
    + 'named under “The Solution” (Strategy, Value, Data, Technology, Capabilities, '
    + 'Governance), which are the AI Readiness Assessment’s and not the DMI’s six. Four '
    + 'deliberate differences from the reference, each of them a template-wide decision '
    + 'rather than this page’s: the header eyebrow reads “Digitopia / Customer Success Story” '
    + 'where digitopia.co reads “SUCCESS STORIES”; the headline is the company name, so the '
    + 'reference’s own “Fueling Scalable AI Adoption with Data-Led Strategy” survives only in '
    + 'the PDF’s title and in the standfirst under it — say the word and it can become this '
    + 'page’s H1; the reference repeats a “Read More” button inside the Solution and Impact '
    + 'panels, which here is the one “Read the full story (PDF)” button in the header, '
    + 'pointing at the same document; and “The Impact” runs as a single list whose two bold '
    + 'labels group it, rather than as two lists under a shared heading. One thing to look '
    + 'at rather than decide: the left half of the reference’s Impact panel is blank in the '
    + 'capture, and the fourth photograph supplied with it (the woman at the laptop) is the '
    + 'only one with nowhere else to go, so it is placed there — confirm that is where it '
    + 'belongs. Lastly, the two assessment photographs are 663–768px originals, which is '
    + 'what the live site serves; they are drawn at that width and will look soft on a '
    + 'high-density screen until larger files arrive.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 2
